import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "@apollo/client/react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ItemCountBadge from "../ItemCountBadge";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { INVENTORY_REQUEST_QUERY, FETCH_PROJECT_QUERY } from "src/constants/graphql/queries";
import InventoryTable from "src/views/inventory/requests/orders/InventoryTable";
import ManualLocationKit from "./manual-location-kits/ManualLocationKit";
import GuidedPickingModal from "./GuidedPickingModal";
import OutboundOrderGuidedWizardModal from "./outbound-order-guided-wizard/OutboundOrderGuidedWizardModal";
import useMutation from "../../../../hooks/useMutation";
import { UPDATE_INVENTORY_REQUEST_MUTATION } from "src/constants/graphql/mutations";
import Status from "../../header/Status";
import Header from "../../header/Header";
import { isVaSubdomain } from "src/utils/inventoryRequests";
import PalletsBuiltField from "./PalletsBuiltField";
import { getInventoryRequestFields } from "src/views/inventory/utils";
import useInventoryItemsFilter from "../../../../hooks/useInventoryItemsFilter";
import useHasAdditionalCharges from "src/components/capture/ReceivingWizard/hooks/useHasAdditionalCharges";
import BillingQuantitiesModal from "src/components/capture/ReceivingWizard/Shipment/Billing/BillingQuantitiesModal";
import { getOutboundBillingType } from "src/utils/billingHelpers";
import { fullWidthDialog } from "src/constants/dialog";
import useRoles from "../../../../hooks/user/useRoles";

export default function OrderPickingModal({ id, onClose }) {
  const navigate = useNavigate();
  const hasAssistantPermission = useRoles(["admin", "manager", "inventory"]);
  const [openPalletsBuiltCountModal, setOpenPalletsBuiltCountModal] = useState(false);
  const [openBillingQuantitiesModal, setOpenBillingQuantitiesModal] = useState(false);
  const { loading, data: { inventoryRequest = {} } = {} } = useQuery(INVENTORY_REQUEST_QUERY, {
    variables: { id },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: false,
  });
  const [saveInventoryRequest] = useMutation(UPDATE_INVENTORY_REQUEST_MUTATION);
  const [openKitModal, setOpenKitModal] = useState(false);
  const [kitRackLocations, setKitRackLocations] = useState([]);
  const [selectedKitAssetId, setSelectedKitAssetId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showGuidedPicking, setShowGuidedPicking] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const isVa = isVaSubdomain();
  const { data: { project = {} } = {} } = useQuery(FETCH_PROJECT_QUERY, {
    variables: { id: inventoryRequest?.projectId },
    skip: !inventoryRequest?.projectId,
    fetchPolicy: "cache-first", // Use cache to avoid repeated queries
  });
  const hasAdditionalCharges = useHasAdditionalCharges(project, "outbound");
  const billingType = useMemo(() => getOutboundBillingType(project), [project]);

  const inventoryItems = inventoryRequest.inventoryItems || [];

  // Clone inventoryItems array to avoid mutating frozen Apollo cache objects
  const sortedInventoryItems = [...inventoryItems].sort((a, b) => {
    const aIsKit = a.asset?.type === "Kit";
    const bIsKit = b.asset?.type === "Kit";

    if (aIsKit && !bIsKit) return 1;
    if (!aIsKit && bIsKit) return -1;
    if (aIsKit && bIsKit) return 0;

    const aLocation = a.rackLocations[0]?.warehouse_locations?.[0]?.location_name || "";
    const bLocation = b.rackLocations[0]?.warehouse_locations?.[0]?.location_name || "";
    return aLocation.localeCompare(bLocation);
  });

  const {
    searchTerm,
    filteredItems: filteredInventoryItems,
    handleSearchChange,
  } = useInventoryItemsFilter(sortedInventoryItems);

  // Create warehouse lookup map from warehouseId to name
  const warehouseLookup = useMemo(() => {
    const lookup = {};
    if (inventoryRequest.warehousesFromInventoryItems) {
      inventoryRequest.warehousesFromInventoryItems.forEach(warehouse => {
        lookup[warehouse.warehouseId] = warehouse.name;
      });
    }
    return lookup;
  }, [inventoryRequest.warehousesFromInventoryItems]);

  // Group items by warehouse for visual separation
  const itemsGroupedByWarehouse = useMemo(() => {
    const grouped = {};
    filteredInventoryItems.forEach(item => {
      const warehouseIds = item.rackLocations?.map(rl => rl.warehouse?.warehouse_id).filter(Boolean) || [];
      const uniqueWarehouseIds = [...new Set(warehouseIds)];

      if (uniqueWarehouseIds.length === 0) {
        // If no warehouse specified, group under 'Unknown'
        if (!grouped["Unknown"]) grouped["Unknown"] = [];
        grouped["Unknown"].push(item);
      } else {
        // Group item under each warehouse it belongs to (using warehouse name as key)
        uniqueWarehouseIds.forEach(warehouseId => {
          const warehouseName = warehouseLookup[warehouseId] || warehouseId;
          if (!grouped[warehouseName]) grouped[warehouseName] = [];
          grouped[warehouseName].push(item);
        });
      }
    });
    return grouped;
  }, [filteredInventoryItems, warehouseLookup]);

  const hasMultipleWarehouses = Object.keys(itemsGroupedByWarehouse).length > 1;

  const showBarcodeColumn = useMemo(() => {
    return filteredInventoryItems.some(item => item.asset?.barcode);
  }, [filteredInventoryItems]);

  const handlePickKit = (kitAssetId, rackLocations, item) => {
    setSelectedKitAssetId(kitAssetId);
    setKitRackLocations(rackLocations);
    setSelectedItem(item);
    setOpenKitModal(true);
  };

  const changeStatus = useCallback(
    key => async checked => {
      const input = {
        vaStatus: {
          [key]: checked,
        },
      };
      await saveInventoryRequest({
        variables: { id: inventoryRequest.id, input },
      });
    },
    [inventoryRequest, hasAdditionalCharges, saveInventoryRequest]
  );

  const handleStartPicking = () => {
    setShowGuidedPicking(true);
  };

  const handleGuidedPickingClose = () => {
    setShowGuidedPicking(false);
  };

  const handlePalletsBuiltCountSubmit = billingQuantities => {
    saveInventoryRequest({
      variables: {
        id: inventoryRequest.id,
        input: getInventoryRequestFields({
          ...inventoryRequest,
          billingQuantities,
        }),
      },
    });
  };

  const toggleBillingModal = () => {
    setOpenBillingQuantitiesModal(v => !v);
  };

  const allItemsPicked = filteredInventoryItems.every(item => item.pickedStatus);

  const handleStartOutboundAssistant = () => {
    const params = new URLSearchParams(window.location.search);
    params.set("outboundOrderGuidedWizard", id);
    params.set("step", "details");
    navigate({ search: params.toString() });
  };

  const handleOutboundAssistantClose = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete("outboundOrderGuidedWizard");
    params.delete("step");
    navigate({ search: params.toString() });
  };

  return (
    <>
      <Dialog open fullWidth maxWidth="md" onClose={onClose} sx={fullWidthDialog}>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box width={40} />
            <Box display="flex" alignItems="center" gap={1}>
              <Typography color="textPrimary" fontSize={24} fontWeight={500}>
                {isVa ? "Task Order Picking" : "Order Picking"}
              </Typography>
              <ItemCountBadge count={inventoryItems.length || 0} />
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ minHeight: 200 }}>
          <DialogContentText>
            {!loading && (
              <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
                <Header item={inventoryRequest} />
                {/* COMMENTED OUT: Billing modal temporarily disabled for backend work
                <Button variant="outlined" color="primary" onClick={toggleBillingModal}>
                  Billing Info
                </Button>
                */}
              </Box>
            )}
            <Box
              mb={{ sx: 1, md: 3 }}
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Status
                loading={loading}
                inventoryRequest={inventoryRequest}
                changeStatus={changeStatus}
                orderType="inventoryRequest"
                project={project}
              />
              <Box maxWidth={400}>
                <PalletsBuiltField
                  projectId={inventoryRequest.projectId}
                  value={inventoryRequest.billingQuantities}
                  open={openPalletsBuiltCountModal}
                  setOpen={setOpenPalletsBuiltCountModal}
                  onSubmit={handlePalletsBuiltCountSubmit}
                />
              </Box>
            </Box>

            {loading ? (
              <Box width="100%" display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : sortedInventoryItems.length === 0 ? (
              <Box pt={7} display="flex" justifyContent="center">
                No items to pick
              </Box>
            ) : (
              <>
                <Box
                  pb={2}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={1}
                  flexDirection={{
                    xs: "column",
                    sm: "row",
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: 320 }}
                  />
                  {hasAssistantPermission && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleStartOutboundAssistant}
                    >
                      Outbound Order Assistant
                    </Button>
                  )}
                </Box>
                {filteredInventoryItems.length === 0 && searchTerm ? (
                  <Box pt={4} pb={4} display="flex" justifyContent="center">
                    <Typography variant="body2" color="textSecondary">
                      No items found matching "{searchTerm}"
                    </Typography>
                  </Box>
                ) : hasMultipleWarehouses ? (
                  // Render separate tables for each warehouse
                  Object.entries(itemsGroupedByWarehouse).map(
                    ([warehouseName, warehouseItems], index) => (
                      <Box key={warehouseName}>
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 0.5,
                            p: 1.5,
                            backgroundColor: "#f5f5f5",
                            borderRadius: 1,
                            fontWeight: 500,
                            color: "#1976d2",
                          }}
                        >
                          {warehouseName === "Unknown"
                            ? "Items without warehouse assignment"
                            : `Picking from: ${warehouseName}`}
                        </Typography>
                        <InventoryTable
                          filteredInventoryItems={warehouseItems}
                          showBarcodeColumn={showBarcodeColumn}
                          inventoryRequest={inventoryRequest}
                          advancedPickingMode={false}
                          selectedRowId={selectedRowId}
                          setSelectedRowId={setSelectedRowId}
                          handlePickKit={handlePickKit}
                          project={project}
                          warehouseLookup={warehouseLookup}
                          hasMultipleWarehouses={hasMultipleWarehouses}
                          orderType="inventoryRequest"
                        />
                      </Box>
                    )
                  )
                ) : (
                  // Single table for single warehouse
                  <InventoryTable
                    filteredInventoryItems={filteredInventoryItems}
                    showBarcodeColumn={showBarcodeColumn}
                    inventoryRequest={inventoryRequest}
                    advancedPickingMode={false}
                    selectedRowId={selectedRowId}
                    setSelectedRowId={setSelectedRowId}
                    handlePickKit={handlePickKit}
                    project={project}
                    warehouseLookup={warehouseLookup}
                    hasMultipleWarehouses={hasMultipleWarehouses}
                    orderType="inventoryRequest"
                  />
                )}
              </>
            )}
          </DialogContentText>
        </DialogContent>
      </Dialog>

      {openKitModal && (
        <ManualLocationKit
          inventoryRequestId={id}
          assetId={selectedKitAssetId}
          rackLocations={kitRackLocations}
          item={selectedItem}
          onClose={() => setOpenKitModal(false)}
          orderType="inventoryRequest"
        />
      )}

      {showGuidedPicking && (
        <GuidedPickingModal
          billingType={billingType}
          inventoryRequestId={id}
          inventoryRequest={inventoryRequest}
          inventoryItems={filteredInventoryItems}
          onClose={handleGuidedPickingClose}
          onComplete={() => {}}
          orderType="inventoryRequest"
        />
      )}

      {new URLSearchParams(window.location.search).get("outboundOrderGuidedWizard") && (
        <OutboundOrderGuidedWizardModal
          inventoryRequest={inventoryRequest}
          onToggle={handleOutboundAssistantClose}
        />
      )}

      {openBillingQuantitiesModal && (
        <BillingQuantitiesModal
          type="Outbound"
          billingType={billingType}
          shipment={inventoryRequest}
          palletTypes={project?.palletTypes}
          unitTypes={project?.unitTypes}
          onSave={toggleBillingModal}
          onClose={toggleBillingModal}
        />
      )}
    </>
  );
}
